import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  UserDataType,
  PermissionsEnum,
  UserProfileType,
  BusinessDataType,
  DeviceDetailsType,
  QuickBooksSyncType,
  BusinessFinancialDefaultsType,
} from '@am3nd-org/pointcraft-types';

const initialUserData: UserDataType = {
  profile: {
    currentUser: null,
    allUsers: [],
    email: '',
  },
  business: {
    id: '',
    name: '',
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
    },
    email: '',
    phone: '',
    features: [],
    permissions: [],
    website: '',
    logoUrl: '',
    offlineModeEnabled: false,
    locations: [],
    financialDefaults: {
      currency: '',
      taxRate: 0,
      limits: {
        web: {
          paymentLinks: 0,
        },
        cardPayments: {
          dailyMaxInCents: 0,
          perTransactionInCents: 0,
        },
      },
      processingConfig: {
        rate: 0.039,
        flatFeeExemptions: [],
        flatFeeRules: [],
        customerSplits: {
          rate: 0,
          fixed: 0,
        },
      },
      canCarryBalance: true,
      rollingBalanceLimit: 0,
      acceptTips: false,
      payments: undefined,
      // {
      //   inPersonPayments: {
      //     provider: AllPaymentProvidersEnum.STRIPE,
      //     enabled: false,
      //   },
      //   webPayments: {
      //     provider: AllPaymentProvidersEnum.STRIPE,
      //     enabled: false,
      //   },
      //   achPayments: {
      //     provider: AllPaymentProvidersEnum.STRIPE,
      //     enabled: false,
      //   },
      // },
    },
    productDefaults: {
      // categories: [],
      tags: [],
    },
    configurations: {
      businessType: '',
      features: [],
      permissions: [],
      offlineModeEnabled: false,
      mccType: '',
      canProcessDebts: false,
      canProcessTopUps: false,
      timeZone: '',
      quickBooksSyncEnabled: false,
      quickBooksSyncType: QuickBooksSyncType.NIGHTLY,
      itemCategories: ['GENERAL'],
    },
    system_pin: '0000',
    isSuperAdmin: false,
  },
  currentDevice: {
    businessLocationId: '',
    currentlyOffline: false,
    lastSync: 0,
    updateRequired: false,
  },
  auth: {
    token: null,
  },
  system: {
    activeFeatures: [],
    server: {
      environment: 'production',
      version: '0.0.0',
      status: 'offline',
    },
  },
  updating: false,
};

interface UserDataStore extends UserDataType {
  setUserProfile: (value: UserProfileType) => void;
  setCurrentDevice: (value: DeviceDetailsType) => void;
  setBusinessData: (value: BusinessDataType) => void;
  setAuthorizedFeatures: (value: string[]) => void;
  setUserPermissions: (userId: string | null, value: PermissionsEnum[]) => void;
  setToken: (value: string) => void;
  setLastSync: (value: number) => void;
  setUpdateRequired: (value: boolean) => void;
  setOfflineModeEnabled: (value: boolean) => void;
  isUserSignedIn: () => boolean;
  signUserOut: () => UserDataType;
  setFetchingUserProfile: (value: boolean) => void;
  setUserData: (userData: UserDataType) => void;
  updateFinancialDefaults: (value: BusinessFinancialDefaultsType) => void;
}

const useUserDataStore = create<UserDataStore>()(
  persist(
    (set, get) => ({
      ...initialUserData,
      setUserProfile: (value) => set({ profile: value }),
      setCurrentDevice: (value) => set({ currentDevice: value }),
      setBusinessData: (value) =>
        set((state) => {
          console.log('AZED setBusinessData', value);
          console.log('AZED state.business', state.business);
          return {
            business: {
              ...state.business,
              ...value,
            },
          };
        }),
      setAuthorizedFeatures: (value) => set({ system: { ...get().system, activeFeatures: value } }),
      setUserPermissions: (userId, value) =>
        set((state) => {
          const updatedAllUsers = state.profile.allUsers.map((user) =>
            user.userId === userId ? { ...user, permissions: value } : user
          );
          const updatedCurrentUser =
            state.profile.currentUser?.userId === userId
              ? { ...state.profile.currentUser, permissions: value }
              : state.profile.currentUser;
          return {
            profile: {
              ...state.profile,
              allUsers: updatedAllUsers,
              currentUser: updatedCurrentUser,
            },
          };
        }),
      setToken: (value) => set({ auth: { token: value } }),
      setLastSync: (value) => set({ currentDevice: { ...get().currentDevice, lastSync: value } }),
      setUpdateRequired: (value) =>
        set({ currentDevice: { ...get().currentDevice, updateRequired: value } }),
      setOfflineModeEnabled: (value) =>
        set({ business: { ...get().business, offlineModeEnabled: value } }),
      isUserSignedIn: () => !!get().auth.token,
      signUserOut: () => {
        set(initialUserData);
        return initialUserData;
      },
      setFetchingUserProfile: (value) => set({ updating: value }),

      // fetchUserData: () => {
      //   // Fetch user data from the server
      // }
      setUserData: (userData) => {
        set({
          ...userData,
        });
      },
      updateFinancialDefaults: (value) =>
        set((state) => ({
          business: {
            ...state.business,
            financialDefaults: {
              ...state.business.financialDefaults,
              taxRate: value.taxRate,
              processingConfig: {
                ...state.business.financialDefaults.processingConfig,
                customerSplits: value.processingConfig.customerSplits,
              },
            },
          },
        })),
    }),
    {
      name: 'user-data-storage',
      partialize: (state) => ({ auth: state.auth }),
    }
  )
);

export default useUserDataStore;

// const options = {
//   revalidateIfStale: false,
//   revalidateOnFocus: false,
//   revalidateOnReconnect: false,
// };
// export const useUserData = (shouldFetch: boolean) => {
//   const { data, error } = useSWR<UserDataType>(shouldFetch ? '/api/user' : null, fetcher, {
//     ...options,
//     // enabled: shouldFetch,
//     // revalidateIfStale: false,
//   });
//   return {
//     userData: data,
//     isLoading: !error && !data,
//     isError: error,
//   };
// };

export const hasPermission = (permission: PermissionsEnum) => {
  const { profile, business } = useUserDataStore.getState();
  console.log('business', business);
  return business.configurations.permissions.includes(permission);
};

export const businessHasPermission = (permission: PermissionsEnum) => {
  const { business } = useUserDataStore.getState();
  console.log('businessHasPermission check:', {
    permission,
    permissions: business.configurations.permissions,
    includes: business.configurations.permissions.includes(permission),
  });
  return business.configurations.permissions.includes(permission);
};
